import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { evictFolderContents, getFolderPathFromCache, getInMemoryCache } from '@/lib/cache-utils';

/** Maps GROUP event subType → Apollo __typename values to evict */
const SUBTYPE_TO_TYPENAMES: Record<string, string[]> = {
  device: ['Device', 'Group'],
  playlist: ['Playlist', 'Group'],
  schedule: ['Schedule', 'Group'],
  asset: ['Asset', 'Group'],
  engage: ['Engage', 'Group'],
};

/**
 * Listens for real-time GROUP DELETE events (broadcast via `realtime:event` CustomEvent)
 * and cascade-evicts all cached entities inside the deleted folder.
 *
 * Uses the event's `subType` (device/playlist/schedule/asset) to determine which
 * entity types to evict, so deleting a playlist folder doesn't evict cached schedules.
 *
 * This handles the case where another user deletes a folder — the WS event arrives,
 * list queries are refetched by useRealtimeSubscription, but individual entity detail
 * cache entries (persisted in IndexedDB) would otherwise remain stale.
 *
 * Mount once at app level (e.g., AppShell) — handles all entity types via SUBTYPE_TO_TYPENAMES.
 */
export function useFolderDeleteCacheEviction() {
  const cache = getInMemoryCache(useApolloClient());

  useEffect(() => {
    const handler = (e: Event) => {
      const event = (e as CustomEvent).detail;
      if (!event || event.type?.toLowerCase() !== 'group' || event.mutation !== 'DELETE') return;

      const typenames = SUBTYPE_TO_TYPENAMES[event.subType?.toLowerCase()];
      if (!typenames) return;

      // Extract cache once and pass to both lookup and eviction.
      // Must happen BEFORE evicting the folder entity (getFolderPathFromCache reads it).
      const snapshot = cache.extract();
      const folderPath = getFolderPathFromCache(cache, event._id, snapshot);
      if (folderPath) {
        evictFolderContents(cache, folderPath, typenames, snapshot);
      }

      // Also evict the folder Group entity itself so useCurrentGroup sees it
      // disappear immediately (triggers redirect to root). Without this, the
      // folder entity stays cached (its path = parentPath, not folderPath) and
      // the redirect only fires after the 500ms debounced refetch.
      const folderCacheId = cache.identify({ __typename: 'Group', _id: event._id });
      if (folderCacheId) {
        cache.evict({ id: folderCacheId });
        // gc() if evictFolderContents didn't already run it (no children found)
        if (!folderPath) cache.gc();
      }
    };

    window.addEventListener('realtime:event', handler);
    return () => window.removeEventListener('realtime:event', handler);
  }, [cache]);
}
